/**
 * The one in the room that is not like the others.
 *
 * Fifteen enemy types is a good roster, and it is still a roster: after eight
 * floors the player has seen every question it can ask and the only thing that
 * changes is how many are asking. Adding a sixteenth type does not fix that —
 * it buys one floor of novelty and then joins the pool.
 *
 * An elite is a *rule* laid over a type the player already knows. A Brute is a
 * problem they have solved; a Brute that heals itself once when you nearly
 * have it is the same problem with the answer changed, and they can work out
 * the new answer from what they already know rather than being taught it. Four
 * rules across fifteen types is sixty fights, and every one of them starts
 * from something familiar.
 *
 * Each rule is chosen so that its counter is a *verb*, not a stat. None of
 * them can be beaten by having more damage: they are beaten by committing,
 * by making space, by changing target, or by breaking line of sight.
 */

export type Elite = 'revenant' | 'brood' | 'phase' | 'siphon';

export interface EliteRule {
  name: string;
  /** One line, said once, in the player's terms. */
  text: string;
  /** How much tougher, before the marked-target bonus. */
  health: number;
}

export const ELITES: Record<Elite, EliteRule> = {
  /*
   * Heals itself once, at the moment you nearly have it.
   *
   * The answer is to commit — burst it through the threshold rather than
   * chipping at it — which is the opposite of what a player low on health
   * wants to do, and therefore a real decision rather than a damage check.
   */
  revenant: {
    name: 'Revenant',
    text: 'It comes back once. Push it through, or start again.',
    health: 2.2,
  },
  /*
   * Comes apart into three when it dies.
   *
   * Turns "kill it" into "kill it *somewhere*", because three fast things
   * appearing at contact range is a different problem from three appearing
   * across the room. The counter is space, which is a movement decision.
   */
  brood: {
    name: 'Brood',
    text: 'It comes apart when it dies. Mind where you are standing.',
    health: 1.8,
  },
  /*
   * Steps sideways when it is hit hard.
   *
   * Punishes the single big shot and rewards anything that covers ground —
   * a shotgun, a burst, a weapon with spread. It is the one elite that makes
   * a player want a different gun rather than a different plan.
   */
  phase: {
    name: 'Phase',
    text: 'A heavy hit moves it. Something with spread does better.',
    health: 1.7,
  },
  /*
   * Drains you for as long as it can see you.
   *
   * The only elite whose counter is the geometry: break the line and it stops.
   * It also makes an open room genuinely dangerous, which is the thing a map
   * full of cover has no other way of saying.
   */
  siphon: {
    name: 'Siphon',
    text: 'It drains you while it can see you. Break the line.',
    health: 1.6,
  },
};

/** How far a phase step carries it, in metres. */
export const PHASE_STEP = 4.5;
/** Fraction of a hit that has to land at once to move a phase elite. */
export const PHASE_TRIGGER = 0.12;
/** What a revenant comes back to, as a fraction of its maximum. */
export const REVENANT_BACK = 0.62;
/** Below this fraction it turns. */
export const REVENANT_AT = 0.34;
/** Health per second a siphon takes, and how far it reaches. */
export const SIPHON_RATE = 3.4;
export const SIPHON_RANGE = 17;
/** What a brood leaves behind. */
export const BROOD_SPAWN = 'runner';
export const BROOD_COUNT = 3;

/**
 * Which elite a floor gets, if any.
 *
 * Deterministic in the depth and the seed. Never before floor four — an elite
 * is a rule change and a player who has not yet learned the base rules has
 * nothing to change — and never more than one at a time, because two rules on
 * one floor is a fight nobody can read.
 */
export function eliteFor(depth: number, rand: () => number): Elite | null {
  if (depth < 4) return null;
  const chance = Math.min(0.75, 0.25 + (depth - 4) * 0.06);
  if (rand() > chance) return null;
  const pool: Elite[] = depth < 6 ? ['revenant', 'brood'] : ['revenant', 'brood', 'phase', 'siphon'];
  return pool[Math.floor(rand() * pool.length)] ?? null;
}
